import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Square, CheckCircle, Pause, Play, Moon, Sun, Calendar, SkipForward, Timer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Task, TimerMode } from '@/types/task';
import { GradientOrbs } from './focus/GradientOrbs';
import { DeepProgressRing } from './focus/DeepProgressRing';
import { getStarterQuote, getNextMomentumQuote, getRandomMomentumIndex } from '@/lib/quotes';
import { usePomodoroTime } from '@/hooks/usePomodoroTimer';

const QUOTE_CYCLE_DURATION = 20000; // 20 seconds for manager mode

function formatTime(seconds: number) {
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hours > 0) {
    return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/** Inner component that subscribes only to ticking time so GradientOrbs/layout do not re-render every second */
function ImmersiveFocusTimerDisplay({
  timerState,
  sessionDuration,
  breakDuration,
  activeTask,
  currentQuote,
  isFading,
  isBreak,
}: {
  timerState: 'running' | 'paused' | 'break';
  sessionDuration: number;
  breakDuration: number;
  activeTask: Task | null;
  currentQuote: string;
  isFading: boolean;
  isBreak: boolean;
}) {
  const { timeRemaining, elapsedTime } = usePomodoroTime();
  const totalSeconds = timerState === 'break' ? breakDuration * 60 : sessionDuration * 60;
  const progress = totalSeconds > 0 ? Math.min((elapsedTime / totalSeconds) * 100, 100) : 0;

  return (
    <>
      <DeepProgressRing progress={progress} size={300} strokeWidth={2}>
        <div className="flex flex-col items-center justify-center text-center px-6 max-w-[240px]">
          {isBreak ? (
            <div className="mb-5">
              <p className="text-[9px] font-semibold uppercase tracking-[2px] mb-1.5 text-muted-foreground/50">
                Take a moment
              </p>
              <p className="text-sm font-medium leading-snug text-foreground/60">
                Rest & Recharge
              </p>
            </div>
          ) : activeTask && (
            <div className="mb-5">
              <p className="text-[9px] font-semibold uppercase tracking-[2px] mb-1.5 text-muted-foreground/50">
                Working on
              </p>
              <p className="text-sm font-medium leading-snug text-foreground/60">
                {activeTask.content}
              </p>
            </div>
          )}

          {!isBreak && (
            <AnimatePresence mode="wait">
              <motion.p
                key={currentQuote}
                initial={{ opacity: 0 }}
                animate={{ opacity: isFading ? 0 : 0.95 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.8, ease: 'easeInOut' }}
                className="text-[22px] font-light leading-relaxed text-foreground/95"
                style={{ letterSpacing: '-0.3px' }}
              >
                {currentQuote}
              </motion.p>
            </AnimatePresence>
          )}

          {isBreak && (
            <p className="text-4xl font-light tracking-tight text-foreground/80">
              {formatTime(timeRemaining)}
            </p>
          )}
        </div>
      </DeepProgressRing>

      {!isBreak && (
        <div className="mt-6 text-center">
          <p className="text-4xl font-light tracking-tight text-foreground/80">
            {formatTime(timeRemaining)}
          </p>
          <p className="text-xs text-muted-foreground/50 mt-1">remaining</p>
        </div>
      )}
    </>
  );
}

interface ImmersiveFocusModeProps {
  activeTask: Task | null;
  timerState: 'running' | 'paused' | 'break';
  sessionDuration: number;
  breakDuration: number;
  timerMode: TimerMode | null;
  onPause: () => void;
  onResume: () => void;
  onStop: () => void;
  onSkipBreak: () => void;
  onFinishTask?: () => void;
}

export function ImmersiveFocusMode({
  activeTask,
  timerState,
  sessionDuration,
  breakDuration,
  timerMode,
  onPause,
  onResume,
  onStop,
  onSkipBreak,
  onFinishTask,
}: ImmersiveFocusModeProps) {
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'));
  const [currentQuote, setCurrentQuote] = useState(() => getStarterQuote());
  const [momentumIndex, setMomentumIndex] = useState(() => getRandomMomentumIndex());
  const [isFading, setIsFading] = useState(false);

  const toggleTheme = () => {
    const newIsDark = !isDark;
    setIsDark(newIsDark);
    document.documentElement.classList.toggle('dark', newIsDark);
  };

  const quoteTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>();
  useEffect(() => {
    if (timerState === 'paused' || timerState === 'break') return;

    const interval = setInterval(() => {
      setIsFading(true);
      if (quoteTimeoutRef.current) clearTimeout(quoteTimeoutRef.current);
      quoteTimeoutRef.current = setTimeout(() => {
        const { quote, nextIndex } = getNextMomentumQuote(momentumIndex);
        setCurrentQuote(quote);
        setMomentumIndex(nextIndex);
        setIsFading(false);
      }, 800);
    }, QUOTE_CYCLE_DURATION);

    return () => {
      clearInterval(interval);
      if (quoteTimeoutRef.current) {
        clearTimeout(quoteTimeoutRef.current);
        quoteTimeoutRef.current = undefined;
      }
    };
  }, [timerState, momentumIndex]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        e.preventDefault();
        if (timerState === 'paused') {
          onResume();
        } else if (timerState === 'running') {
          onPause();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onPause, onResume, timerState]);

  const isBreak = timerState === 'break';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="fixed inset-0 z-50 flex flex-col bg-background"
    >
      {/* Animated gradient orbs background */}
      <GradientOrbs />

      {/* Header */}
      <header className="absolute top-14 left-0 right-0 flex items-center justify-between px-6 z-20">
        <div className="w-10" />
        {/* Mode badge */}
        {isBreak ? (
          <div 
            className="flex items-center gap-2 px-4 py-2 rounded-full"
            style={{
              background: 'hsl(var(--success) / 0.15)',
              border: '1px solid hsl(var(--success) / 0.3)',
            }}
          >
            <Timer className="h-4 w-4" style={{ color: 'hsl(var(--success))' }} />
            <span 
              className="text-xs font-semibold tracking-wider uppercase"
              style={{ color: 'hsl(var(--success))' }}
            >
              Break
            </span>
          </div>
        ) : (
          <div 
            className="flex items-center gap-2 px-4 py-2 rounded-full"
            style={{
              background: 'hsl(45 93% 47% / 0.15)',
              border: '1px solid hsl(45 93% 47% / 0.3)',
            }}
          >
            <Calendar className="h-4 w-4" style={{ color: 'hsl(45 93% 47%)' }} />
            <span 
              className="text-xs font-semibold tracking-wider uppercase"
              style={{ color: 'hsl(45 93% 47%)' }}
            >
              Manager
            </span>
          </div>
        )}

        {/* Theme toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          className="h-10 w-10 rounded-xl bg-muted/50 border border-border/50 hover:bg-muted backdrop-blur-sm"
        >
          {isDark ? (
            <Sun className="h-5 w-5 text-muted-foreground" />
          ) : (
            <Moon className="h-5 w-5 text-muted-foreground" />
          )}
        </Button>
      </header>

      {/* Main content - timer display subscribes to usePomodoroTime so only it re-renders every second */}
      <main className="flex-1 flex flex-col items-center justify-center px-8 z-10">
        <ImmersiveFocusTimerDisplay
          timerState={timerState}
          sessionDuration={sessionDuration}
          breakDuration={breakDuration}
          activeTask={activeTask}
          currentQuote={currentQuote}
          isFading={isFading}
          isBreak={isBreak}
        />
      </main>

      {/* Controls */}
      <footer className="absolute bottom-20 left-0 right-0 flex items-center justify-center gap-4 px-8 z-20">
        {isBreak ? (
          /* Break controls */
          <button
            onClick={onSkipBreak}
            className="flex items-center gap-2.5 px-5 py-3 rounded-xl border bg-muted/50 border-border/50 transition-transform hover:scale-[1.02] active:scale-[0.98]"
          >
            <SkipForward className="h-5 w-5 text-foreground shrink-0" />
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Skip Break
            </span>
          </button>
        ) : (
          /* Work session controls */
          <>
            {/* Pause/Resume */}
            <button
              type="button"
              onClick={timerState === 'paused' ? onResume : onPause}
              className="flex items-center gap-2.5 px-5 py-3 rounded-xl border bg-muted/50 border-border/50 transition-transform hover:scale-[1.02] active:scale-[0.98]"
              aria-label={timerState === 'paused' ? 'Resume timer' : 'Pause timer'}
            >
              {timerState === 'paused' ? (
                <Play className="h-5 w-5 text-foreground fill-foreground shrink-0" />
              ) : (
                <Pause className="h-5 w-5 text-foreground fill-foreground shrink-0" />
              )}
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {timerState === 'paused' ? 'Resume' : 'Pause'}
              </span>
            </button>

            {/* Finish - Primary action */}
            {onFinishTask && activeTask && (
              <button
                type="button"
                onClick={onFinishTask}
                className="flex items-center gap-2.5 px-5 py-3 rounded-xl transition-transform hover:scale-[1.02] active:scale-[0.98]"
                style={{
                  background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                  boxShadow: '0 8px 24px rgba(34, 197, 94, 0.3)',
                }}
                aria-label="Finish task"
              >
                <CheckCircle className="h-5 w-5 text-white shrink-0" />
                <span className="text-[11px] font-semibold uppercase tracking-wider text-white">
                  Finish
                </span>
              </button>
            )}

            {/* Stop */}
            <button
              type="button"
              onClick={onStop}
              data-tour-target="stop-timer-button"
              className="flex items-center gap-2.5 px-5 py-3 rounded-xl border transition-transform hover:scale-[1.02] active:scale-[0.98]"
              style={{
                background: 'rgba(239, 68, 68, 0.15)',
                borderColor: 'rgba(239, 68, 68, 0.3)',
              }}
              aria-label="Stop timer"
            >
              <Square className="h-5 w-5 fill-destructive text-destructive shrink-0" />
              <span className="text-[11px] font-semibold uppercase tracking-wider text-destructive">
                Stop
              </span>
            </button>
          </>
        )}
      </footer>
    </motion.div>
  );
}
